import z from "zod";

import { Category, Product } from "@/shared/domains";
import { PAYMENT_METHOD_TYPE, PAYMENT_ORIGIN, PAYMENT_STATUS } from "./enums";

export const PaymentMethodType = z.enum(PAYMENT_METHOD_TYPE);

const CardMetadata = z.object({
  brand: z.string(),
  expireDate: z.string(),
  lastFourDigits: z.string(),
});

const BasePaymentMethod = z.object({
  id: z.string(),
  default: z.boolean().nullable(),
});

const CardPaymentMethod = BasePaymentMethod.extend({
  type: z.literal(PAYMENT_METHOD_TYPE.CARD),
  metadata: CardMetadata,
});

const KlarnaPaymentMethod = BasePaymentMethod.extend({
  type: z.literal(PAYMENT_METHOD_TYPE.KLARNA),
  metadata: z.null(),
});

const ApplePayPaymentMethod = BasePaymentMethod.extend({
  type: z.literal(PAYMENT_METHOD_TYPE.APPLE_PAY),
  metadata: CardMetadata,
});

export const PaymentMethod = z.discriminatedUnion("type", [
  CardPaymentMethod,
  KlarnaPaymentMethod,
  ApplePayPaymentMethod,
]);

export type PaymentMethodType = z.infer<typeof PaymentMethodType>;

export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const PaymentOrigin = z.enum(PAYMENT_ORIGIN);
export type PaymentOrigin = z.infer<typeof PaymentOrigin>;

export const BillingDetails = z.object({
  nextBillingAmount: z.number().nullable(),
  nextBillingDate: z.iso.datetime({ offset: true }).nullable(),
  paymentMethod: PaymentMethod,
});
export type BillingDetails = z.infer<typeof BillingDetails>;

export const Invoice = z.object({
  id: z.string(),
  amount: z.string(),
  status: z.enum(PAYMENT_STATUS),
  date: z.iso.date(),
  detail: z.string(),
  hostedInvoiceUrl: z.url().nullish(),
  resolution: z.string().optional(),
  get productCategory() {
    return Category.pick({
      id: true,
      name: true,
      slug: true,
    }).nullable();
  },
  get product() {
    return Product.pick({ id: true, name: true })
      .extend({ dose: z.string().nullable() })
      .nullable();
  },
  paymentMethod: PaymentMethod.nullable(),
});
export type Invoice = z.infer<typeof Invoice>;
